// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImageProps } from "@guestsupply/MobiusOverrides/LazyImage";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";

const enum fields {
    showImages = "showImages",
    showOnlyTopLevelCategories = "showOnlyTopLevelCategories",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImages]: boolean;
        [fields.showOnlyTopLevelCategories]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const getCategoriesParameter = {
        maxDepth: props.fields.showOnlyTopLevelCategories ? 1 : 2,
        includeStartCategory: false,
    };
    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface CategoryListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkPresentationProps;
    // XNG-Changes
    categoryContainer?: InjectableCss;
    categoryHeader?: InjectableCss;
}

export const categoryListStyles: CategoryListStyles = {};

const styles = categoryListStyles;

class CategoryList extends React.Component<Props> {
    container = React.createRef<HTMLDivElement>();
    categoryItemStyles: GridItemProps = { ...styles.categoryItem };

    UNSAFE_componentWillMount(): void {
        const { categoriesDataView, getCategoriesParameter, loadCategories } = this.props;
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }

    recalculateWidth = () => {
        if (!this.container.current) {
            return;
        }

        const { breakpoints } = this.props.theme;
        const { clientWidth } = this.container.current;
        let width: GridWidths;
        if (clientWidth < breakpoints.values[1]) {
            width = 12;
        } else if (clientWidth < breakpoints.values[2]) {
            width = 6;
        } else if (clientWidth < breakpoints.values[3]) {
            width = 4;
        } else {
            width = 2;
        }

        if (width === this.categoryItemStyles.width) {
            return;
        }

        this.categoryItemStyles.width = width;
        this.forceUpdate();
    };

    componentDidMount() {
        // this.recalculateWidth();
        // window.addEventListener("resize", this.recalculateWidth, true);
    }

    componentWillUnmount() {
        // window.removeEventListener("resize", this.recalculateWidth);
    }

    componentDidUpdate(prevProps: Props) {
        // if (!prevProps.categoriesDataView.value && this.props.categoriesDataView.value) {
        //     this.recalculateWidth();
        // }
    }

    render() {
        const {
            categoriesDataView,
            topLevelCategoryIds,
            fields: { showImages, showOnlyTopLevelCategories },
        } = this.props;
        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value || !topLevelCategoryIds) {
            return null;
        }

        const categories = categoriesDataView.value.filter(o => topLevelCategoryIds.indexOf(o.id) > -1);

        return (
            <StyledWrapper ref={this.container}>
                <GridContainer {...styles.container}>
                    {categories.map(category => (
                        <GridItem key={category.id.toString()} {...this.categoryItemStyles}>
                            <StyledWrapper {...styles.innerContainer}>
                                <StyledWrapper {...styles.categoryContainer}>
                                    <StyledWrapper
                                        {...styles.categoryHeader}
                                        className={category.subCategoryIds?.length ? "has-subcategories" : ""}
                                    >
                                        {showImages && category.smallImagePath && (
                                            <Link href={category.path} {...styles.categoryImageLink}>
                                                <LazyImage
                                                    width="160"
                                                    height="160"
                                                    src={category.smallImagePath}
                                                    altText={category.imageAltText}
                                                    customTransformations={{ default: "GUEST_Category_160x160" }}
                                                    {...styles.categoryImage}
                                                />
                                            </Link>
                                        )}
                                        <Link
                                            href={category.path}
                                            {...styles.subCategoryNameLink}
                                            data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                                            aria-label={category.shortDescription}
                                        >
                                            {category.shortDescription}
                                        </Link>
                                    </StyledWrapper>
                                    {createWidgetElement("Category/CategoryDetailLinkList", {
                                        fields: {},
                                        ...{ categoryProp: category, maxNumberOfCategories: 5 },
                                    })}
                                </StyledWrapper>
                                <StyledWrapper {...styles.viewMoreLinkItem}>
                                    <Link href={category.path} {...styles.viewMoreLink} aria-label="View More">
                                        {translate("View More")}
                                    </Link>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                    ))}
                </GridContainer>
            </StyledWrapper>
        );
    }
}

const SubCategoryLinkView = ({ category }: ReturnType<typeof linkMapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link href={category.path} {...styles.subCategoryNameLink} aria-label={category.shortDescription}>
            {category.shortDescription}
        </Link>
    );
};

const linkMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(linkMapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CategoryList)),
    definition: {
        group: "Categories",
        icon: "list-ul",
        allowedContexts: ["CategoryListPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showOnlyTopLevelCategories,
                displayName: "Show only top-level categories",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
